<template>
  <section class="travel-passenger">
    <div v-if="journeyStore.isInitialized">
      <div v-if="isDataValid">
        <TravelPlannerHero
          :showChangeButton="true"
          :label="$t('travelPlanner.hero.passengerLabel')"
        />
        <PassengerPicker :initialPassengers="initialPassengers" />
        <NavigationBar>
          <router-link
            class="button button--medium button--secondary"
            :to="backRoute"
          >
            {{ $t('common.actions.back') }}
          </router-link>
          <SearchJourney :nextRoute="nextRoute" />
        </NavigationBar>
      </div>
      <div v-else>
        <router-link :to="backRoute">{{ $t('common.actions.returnToSearch') }}</router-link>
      </div>
    </div>
    <div v-else>
      <p>{{ $t('travelPlanner.loading') }}</p>
      <Spinner size="large" />
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useJourneyStore } from '@/stores/journeyStore';
import { useRoute, useRouter } from 'vue-router';
import { fetchAllStations } from '@/services/journeyService';
import PassengerPicker from '@/components/TravelPlanner/PassengerPicker/PassengerPicker.vue';
import SearchJourney from '@/components/TravelPlanner/SearchJourney/SearchJourney.vue';
import NavigationBar from '@/components/TravelPlanner/NavigationBar/NavigationBar.vue';
import TravelPlannerHero from '@/components/TravelPlanner/TravelPlannerHero.vue';
import Spinner from '@/components/BaseComponents/Spinner/Spinner.vue';
import { isValid } from 'date-fns';
import type { LocationQueryValue } from 'vue-router';

export default defineComponent({
  name: 'TravelPassengers',
  components: {
    TravelPlannerHero,
    PassengerPicker,
    SearchJourney,
    NavigationBar,
    Spinner,
  },
  setup() {
    const journeyStore = useJourneyStore();
    const route = useRoute();
    const initialPassengers = ref([...journeyStore.passengers]);

    const isDataValid = computed(() => {
      const valid = (
        !!journeyStore.OriginStation?.name &&
        !!journeyStore.destinationStation?.name &&
        !!journeyStore.departureDate
      );
      return valid;
    });

    const backRoute = computed(() => {
      const query: Record<string, string | undefined> = {
        origin: journeyStore.OriginStation?._u_i_c_station_code || getQueryString(route.query.origin),
        destination: journeyStore.destinationStation?._u_i_c_station_code || getQueryString(route.query.destination),
        outboundDate: journeyStore.departureDate || getQueryString(route.query.outboundDate),
        returnDate: journeyStore.returnDate || getQueryString(route.query.returnDate) || undefined,
        lang: getQueryString(route.query.lang) || 'no',
      };
      Object.keys(query).forEach((key) => query[key] === undefined && delete query[key]);
      return {
        name: 'Search',
        query,
      };
    });

    const nextRoute = computed(() => {
      const query: Record<string, string | undefined> = {
        origin: journeyStore.OriginStation?._u_i_c_station_code || getQueryString(route.query.origin),
        destination: journeyStore.destinationStation?._u_i_c_station_code || getQueryString(route.query.destination),
        outboundDate: journeyStore.departureDate || getQueryString(route.query.outboundDate),
        returnDate: journeyStore.returnDate || getQueryString(route.query.returnDate) || undefined,
        lang: getQueryString(route.query.lang) || 'no',
        passengers: journeyStore.passengers.length
          ? JSON.stringify(
              journeyStore.passengers.map(p => ({
                id: p.id,
                type: p.type,
                discount_cards: p.discount_cards || [],
                travel_passes: p.travel_passes || [], // Include if needed
              }))
            )
          : undefined,
      };
      Object.keys(query).forEach((key) => query[key] === undefined && delete query[key]);
      return {
        name: 'SearchResultPage',
        query,
      };
    });

    // Type guard to convert LocationQueryValue | LocationQueryValue[] to string | undefined
    const getQueryString = (value: LocationQueryValue | LocationQueryValue[]): string | undefined => {
      if (Array.isArray(value)) {
        console.warn('Unexpected array in query parameter:', value);
        return value.find((v): v is string => typeof v === 'string') || undefined;
      }
      return typeof value === 'string' ? value : undefined;
    };

    // Function to update journeyStore from query parameters
    const updateStoreFromQuery = async (query: Record<string, LocationQueryValue | LocationQueryValue[]>, isInitialLoad = false) => {
      const origin = getQueryString(query.origin);
      const destination = getQueryString(query.destination);
      const outboundDate = getQueryString(query.outboundDate);
      const departureDate = getQueryString(query.departureDate);
      const returnDate = getQueryString(query.returnDate);
      const passengers = getQueryString(query.passengers);

      // Fetch stations if needed (only on initial load)
      if (isInitialLoad && !journeyStore.allStations.length) {
        try {
          const stations = await fetchAllStations();
          journeyStore.setAllStations(stations);
        } catch (error) {
          console.error('Failed to fetch stations:', error);
          return;
        }
      }

      // Update stations only if query parameters differ from current store
      if (origin && (origin !== journeyStore.OriginStation?._u_i_c_station_code || origin !== journeyStore.OriginStation?.name)) {
        const station = journeyStore.allStations.find(
          (s) => s.name === origin || s._u_i_c_station_code === origin
        );
        if (station) {
          journeyStore.setOriginStation(station);
        } else {
          console.warn(`Origin station with name or UIC code ${origin} not found`);
        }
      }

      if (destination && (destination !== journeyStore.destinationStation?._u_i_c_station_code || destination !== journeyStore.destinationStation?.name)) {
        const station = journeyStore.allStations.find(
          (s) => s.name === destination || s._u_i_c_station_code === destination
        );
        if (station) {
          journeyStore.setDestinationStation(station);
        } else {
          console.warn(`Destination station with name or UIC code ${destination} not found`);
        }
      }

      const date = outboundDate || departureDate;
      if (date && date !== journeyStore.departureDate) {
        const isValidDateString = (dateStr: string): boolean => {
          const regex = /^\d{4}-\d{2}-\d{2}$/;
          if (!regex.test(dateStr)) return false;
          const parsed = new Date(dateStr);
          return isValid(parsed);
        };
        if (isValidDateString(date)) {
          journeyStore.setDepartureDate(date);
        } else {
          console.warn('Invalid departure date format:', date);
        }
      }

      if (returnDate && returnDate !== '' && returnDate !== journeyStore.returnDate) {
        const isValidDateString = (dateStr: string): boolean => {
          const regex = /^\d{4}-\d{2}-\d{2}$/;
          if (!regex.test(dateStr)) return false;
          const parsed = new Date(dateStr);
          return isValid(parsed);
        };
        if (isValidDateString(returnDate)) {
          journeyStore.setReturnDate(returnDate);
        } else {
          console.warn('Invalid return date format:', returnDate);
        }
      } else if (!returnDate && journeyStore.returnDate) {
        journeyStore.setReturnDate(null);
      }
      if (passengers) {
        try {
          const parsedPassengers = JSON.parse(passengers);
          if (
            Array.isArray(parsedPassengers) &&
            JSON.stringify(parsedPassengers) !== JSON.stringify(journeyStore.passengers)
          ) {
            journeyStore.setPassengers(
              parsedPassengers.map((p) => ({
                id: p.id || `temp-${Math.random().toString(36).slice(2)}`, // Fallback ID if missing
                type: p.type,
                discount_cards: p.discount_cards || [],
                travel_passes: p.travel_passes || [],
              }))
            );
          }
        } catch (error) {
          console.warn('Failed to parse passengers from query:', passengers, error);
        }
      }
    };

    onMounted(async () => {
      try {
        await updateStoreFromQuery(route.query, true);
        journeyStore.isInitialized = true;
      } catch (error) {
        console.error('Error initializing TravelPassengers:', error);
      }
    });

    watch(
      () => route.query,
      (newQuery) => {
        updateStoreFromQuery(newQuery);
      },
      { deep: true }
    );

    return {
      journeyStore,
      isDataValid,
      backRoute,
      nextRoute,
      initialPassengers,
    };
  },
});
</script>

<style lang="scss" scoped>
.travel-passenger {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
</style>